/**
 * programOnboarding/validation — bounded-outcome grammar (the client mirror of
 * the .NET ReadingCheckRules). Two jobs:
 *  1. Privacy by construction: the ONLY shapes that validate are machine ids,
 *     allow-listed enums, and bounded numbers. A payload that carries typed
 *     text, a transcript, or any free-form string is rejected — so the offline
 *     queue and network layer can never serialize a child's raw answer.
 *  2. Robust fallback: a program profile that fails validation is treated as
 *     absent (legacy behavior), never as partial truth.
 *
 * Pure, throw-free, dependency-free.
 */

import type {
  ChildProgramProfile,
  Concern,
  Confidence,
  Domain,
  DomainClassification,
  EntryBand,
  FoundationsStage,
  ItemOutcome,
  Program,
  ReadingCheckItemResult,
  Recommendation,
  SchoolPlacement,
  SelectionSource,
} from './types';

export const ITEM_ID_RE = /^rc-[a-z0-9-]{1,64}$/;
export const FRONTIER_KEY_RE = /^[a-z]{2}:G[0-9]{1,2}-L[0-9]{1,3}$/;
export const ALLOWLIST_TOKEN_RE = /^[a-z][a-z0-9-]{1,48}$/;
export const FORM_ID_RE = /^[a-z0-9][a-z0-9-]{1,48}$/;

const DOMAINS = new Set<Domain>([
  'pa', 'ls', 'blend', 'dec', 'psw', 'hfw', 'spell', 'morph', 'text', 'flu', 'comp', 'lcomp',
]);
const CLASSIFICATIONS = new Set<DomainClassification>([
  'strong', 'developing', 'needs-support', 'not-measured',
]);
const OUTCOMES = new Set<ItemOutcome>([
  'correct', 'close', 'incorrect', 'dont-know', 'abandoned', 'not-measured',
]);
const PROGRAMS = new Set<Program>(['foundations', 'grade-learning', 'undecided']);
const SOURCES = new Set<SelectionSource>(['parent', 'reading-check', 'parent-override']);
const PLACEMENTS = new Set<SchoolPlacement>([
  'enrolled', 'homeschooled', 'not-in-school', 'unsure',
]);
const BANDS = new Set<EntryBand>(['early', 'middle', 'upper', 'adolescent']);
const STAGES = new Set<FoundationsStage>([
  'pre-alphabetic', 'letter-sounds', 'short-vowels', 'blends-digraphs', 'long-vowels', 'multisyllable-morphology',
]);
const RECOMMENDATIONS = new Set<Recommendation>(['foundations', 'grade-learning', 'more-info']);
const CONFIDENCES = new Set<Confidence>(['high', 'developing', 'more-info']);
const CONCERNS = new Set<Concern>([
  'decoding', 'spelling', 'fluency', 'comprehension', 'keeping-up', 'unsure',
]);

export const isDomain = (v: unknown): v is Domain => typeof v === 'string' && DOMAINS.has(v as Domain);
export const isClassification = (v: unknown): v is DomainClassification =>
  typeof v === 'string' && CLASSIFICATIONS.has(v as DomainClassification);
export const isOutcome = (v: unknown): v is ItemOutcome =>
  typeof v === 'string' && OUTCOMES.has(v as ItemOutcome);
export const isProgram = (v: unknown): v is Program => typeof v === 'string' && PROGRAMS.has(v as Program);
export const isSelectionSource = (v: unknown): v is SelectionSource =>
  typeof v === 'string' && SOURCES.has(v as SelectionSource);
export const isSchoolPlacement = (v: unknown): v is SchoolPlacement =>
  typeof v === 'string' && PLACEMENTS.has(v as SchoolPlacement);
export const isEntryBand = (v: unknown): v is EntryBand => typeof v === 'string' && BANDS.has(v as EntryBand);
export const isFoundationsStage = (v: unknown): v is FoundationsStage =>
  typeof v === 'string' && STAGES.has(v as FoundationsStage);
export const isRecommendation = (v: unknown): v is Recommendation =>
  typeof v === 'string' && RECOMMENDATIONS.has(v as Recommendation);
export const isConfidence = (v: unknown): v is Confidence =>
  typeof v === 'string' && CONFIDENCES.has(v as Confidence);
export const isConcern = (v: unknown): v is Concern => typeof v === 'string' && CONCERNS.has(v as Concern);

function isBoundedInt(v: unknown, min: number, max: number): boolean {
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
}
function isGrade(v: unknown): v is number {
  return isBoundedInt(v, 1, 12);
}

/**
 * Validate + normalize a single item result. Returns null (reject) for anything
 * outside the grammar — CRUCIALLY, an object carrying any extra string field is
 * accepted only after stripping to the four allowed fields, so raw answers can
 * never ride along.
 */
export function validateItemResult(input: unknown): ReadingCheckItemResult | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  if (typeof o.itemId !== 'string' || !ITEM_ID_RE.test(o.itemId)) return null;
  if (!isDomain(o.domain)) return null;
  if (!isBoundedInt(o.position, 0, 200)) return null;
  if (!isOutcome(o.outcome)) return null;
  // Return ONLY the allow-listed fields — deliberately drops any other property.
  return {
    itemId: o.itemId,
    domain: o.domain,
    position: o.position as number,
    outcome: o.outcome,
  };
}

/** True when a value is a valid frontier lesson stable key (or null). */
export function isFrontierKey(v: unknown): v is string | null {
  return v == null || (typeof v === 'string' && FRONTIER_KEY_RE.test(v));
}

/** True when a token is an allow-list-shaped key (focus/strength). */
export function isAllowlistToken(v: unknown): v is string {
  return typeof v === 'string' && ALLOWLIST_TOKEN_RE.test(v);
}

/**
 * Validate a program profile received from the server (or restored locally).
 * Returns null when the shape is invalid → callers fall back to legacy behavior
 * rather than trusting partial data.
 */
export function validateProgramProfile(input: unknown): ChildProgramProfile | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  if (!isBoundedInt(o.version, 0, 1_000_000)) return null;
  const schemaVersion = isBoundedInt(o.schemaVersion, 1, 1000) ? (o.schemaVersion as number) : 1;

  const activeProgram = o.activeProgram == null ? null : isProgram(o.activeProgram) ? o.activeProgram : undefined;
  const selectionSource = o.selectionSource == null ? null : isSelectionSource(o.selectionSource) ? o.selectionSource : undefined;
  const schoolPlacement = o.schoolPlacement == null ? null : isSchoolPlacement(o.schoolPlacement) ? o.schoolPlacement : undefined;
  const overridden = o.overriddenRecommendation == null
    ? null
    : o.overriddenRecommendation === 'foundations' || o.overriddenRecommendation === 'grade-learning'
      ? o.overriddenRecommendation
      : undefined;
  if (activeProgram === undefined || selectionSource === undefined || schoolPlacement === undefined || overridden === undefined) {
    return null;
  }
  const enrolledGrade = o.enrolledGrade == null ? null : isGrade(o.enrolledGrade) ? (o.enrolledGrade as number) : undefined;
  const gradeLearningStartGrade = o.gradeLearningStartGrade == null ? null : isGrade(o.gradeLearningStartGrade) ? (o.gradeLearningStartGrade as number) : undefined;
  if (enrolledGrade === undefined || gradeLearningStartGrade === undefined) return null;
  const lastProgramChangeAt = o.lastProgramChangeAt == null ? null : typeof o.lastProgramChangeAt === 'string' ? o.lastProgramChangeAt : undefined;
  if (lastProgramChangeAt === undefined) return null;

  return {
    schemaVersion,
    version: o.version as number,
    activeProgram,
    selectionSource,
    overriddenRecommendation: overridden,
    schoolPlacement,
    enrolledGrade,
    gradeLearningStartGrade,
    lastProgramChangeAt,
  };
}

/** Normalize a concern selection: dedupe, allow-list, enforce 'unsure' exclusivity. */
export function normalizeConcerns(input: unknown): Concern[] {
  if (!Array.isArray(input)) return [];
  const set = new Set<Concern>();
  for (const v of input) if (isConcern(v)) set.add(v);
  if (set.has('unsure')) return ['unsure'];
  return [...set];
}
